import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAccessTier } from "@/hooks/useAccessTier";
import { musicController } from "@/lib/audio/musicController";
import type { MusicKey } from "@/lib/audio/musicMap";

const ENABLED_KEY = "heirwise.music.enabled";
const VOLUME_KEY = "heirwise.music.masterVolume";

/** Routes where music may play. Mirrors SFX_ROUTES. */
const MUSIC_ROUTES = [
  "/dashboard",
  "/lesson/",
  "/budget-builder",
  "/credit-management",
  "/scam-defense",
];

function pathInMusicScope(pathname: string): boolean {
  return MUSIC_ROUTES.some((p) => pathname === p || pathname.startsWith(p));
}

interface MusicContextValue {
  /** Plays a music scene with crossfade. No-op if same key already playing or out of scope. */
  setSceneMusic: (key: MusicKey | null, opts?: { volume?: number; fadeMs?: number }) => void;
  stop: (opts?: { fadeMs?: number }) => void;
  duck: (factor?: number, ms?: number) => void;
  unduck: (ms?: number) => void;
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  volume: number; // master 0..1
  setVolume: (v: number) => void;
  inScope: boolean;
  unlocked: boolean;
}

const MusicContext = createContext<MusicContextValue>({
  setSceneMusic: () => {},
  stop: () => {},
  duck: () => {},
  unduck: () => {},
  enabled: true,
  setEnabled: () => {},
  volume: 0.5,
  setVolume: () => {},
  inScope: false,
  unlocked: false,
});

function readStoredBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch { /* ignore */ }
  return fallback;
}
function readStoredNumber(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.max(0, Math.min(1, n));
  } catch { /* ignore */ }
  return fallback;
}

export function MusicProvider({ children }: { children: ReactNode }) {
  const { tier } = useAccessTier();
  const location = useLocation();

  const [enabled, setEnabledState] = useState<boolean>(() => readStoredBool(ENABLED_KEY, true));
  const [volume, setVolumeState] = useState<number>(() => readStoredNumber(VOLUME_KEY, 0.5));
  const [unlocked, setUnlocked] = useState(false);

  // Audio is available to any signed-in learner who can reach a music-scope
  // route — beta, paid, and admin all hear music. Only `none` (unauthorized
  // / still resolving) is excluded so we don't autoplay before access is
  // confirmed. Routes themselves are gated by ProtectedRoute upstream.
  const tierAllowed = tier !== "none";
  const routeAllowed = pathInMusicScope(location.pathname);
  const inScope = tierAllowed && routeAllowed;

  // Sync master volume + mute state into controller
  useEffect(() => { musicController.setMasterVolume(volume); }, [volume]);
  useEffect(() => { musicController.setMuted(!enabled); }, [enabled]);

  // Unlock on first user gesture (same pattern as SFX)
  useEffect(() => {
    if (unlocked) return;
    const unlock = () => {
      setUnlocked(true);
      musicController.unlock();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("pointerdown", unlock, { passive: true });
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, [unlocked]);

  // Leaving scope stops any music
  useEffect(() => {
    if (!inScope && musicController.getCurrent()) {
      musicController.stop({ fadeMs: 400 });
    }
  }, [inScope]);

  // Pause music when tab hidden, resume on visible
  useEffect(() => {
    let release: ((ms?: number) => void) | null = null;
    const onVis = () => {
      if (document.hidden) {
        if (!release) release = musicController.pushDuck(0, 200);
      } else if (release) {
        release(400);
        release = null;
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (release) { release(0); release = null; }
    };
  }, []);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    try { localStorage.setItem(ENABLED_KEY, String(v)); } catch { /* ignore */ }
  }, []);
  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    try { localStorage.setItem(VOLUME_KEY, String(clamped)); } catch { /* ignore */ }
  }, []);

  const setSceneMusic = useCallback<MusicContextValue["setSceneMusic"]>((key, opts) => {
    if (!enabled || !inScope) return;
    if (key == null) { musicController.stop(); return; }
    musicController.play(key, opts);
  }, [enabled, inScope]);

  const stop = useCallback((opts?: { fadeMs?: number }) => { musicController.stop(opts); }, []);
  const duck = useCallback((factor?: number, ms?: number) => { musicController.duck(factor, ms); }, []);
  const unduck = useCallback((ms?: number) => { musicController.unduck(ms); }, []);

  const value = useMemo<MusicContextValue>(() => ({
    setSceneMusic, stop, duck, unduck,
    enabled, setEnabled, volume, setVolume,
    inScope, unlocked,
  }), [setSceneMusic, stop, duck, unduck, enabled, setEnabled, volume, setVolume, inScope, unlocked]);

  return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
}

export function useMusicContext() { return useContext(MusicContext); }